/**
 * BioAIAssistant — Assistant IA de bio réservé Premium (a4)
 *
 * Bouton compact « Suggérer avec l'IA » à placer près du champ bio.
 * - Si l'utilisateur n'est pas Premium : ouvre une modale d'upsell.
 * - Sinon : dialogue de génération (choix du ton + hint libre) puis liste
 *   de 3 suggestions à sélectionner. Le clic sur une suggestion appelle
 *   `onApply(text)`.
 *
 * Bio générée en ≤ 220 caractères, ton contrôlé, garde-fous côté backend.
 */
import React, { useEffect, useState } from 'react';
import { Sparkles, Loader2, Lock, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';

const TONES = [
    { value: 'professionnel', label: 'Professionnel' },
    { value: 'chaleureux', label: 'Chaleureux' },
    { value: 'expert', label: 'Expert' },
    { value: 'concis', label: 'Concis' },
];

export default function BioAIAssistant({ currentBio = '', onApply }) {
    const [premium, setPremium] = useState(null);
    const [open, setOpen] = useState(false);
    const [upsell, setUpsell] = useState(false);
    const [tone, setTone] = useState('professionnel');
    const [hint, setHint] = useState('');
    const [loading, setLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get('/reseau/premium/status');
                if (!cancelled) setPremium(r.data);
            } catch (e) { /* non bloquant */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const handleClick = () => {
        if (!premium?.active) {
            setUpsell(true);
            return;
        }
        setOpen(true);
        if (!suggestions.length) {
            // Pré-remplit hint avec bio actuelle si courte
            if (currentBio && currentBio.length < 180 && !hint) setHint(currentBio);
        }
    };

    const generate = async () => {
        setLoading(true);
        try {
            const r = await api.post('/reseau/career/bio/suggest', {
                hint: hint.trim() || null,
                tone,
            });
            const items = r.data?.suggestions || [];
            if (!items.length) {
                toast.error("L'assistant n'a pas produit de suggestion. Réessayez avec plus de contexte.");
            }
            setSuggestions(items);
        } catch (e) {
            if (e?.response?.status === 403) {
                toast.error('Abonnement Réseau Premium requis');
                setOpen(false);
                setUpsell(true);
            } else {
                toast.error("Impossible de générer les suggestions");
            }
        } finally {
            setLoading(false);
        }
    };

    const applyOne = (text) => {
        if (onApply) onApply(text);
        toast.success('Bio insérée dans le formulaire');
        setOpen(false);
    };

    return (
        <>
            <button
                type="button"
                onClick={handleClick}
                data-testid="bio-ai-assistant-btn"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-lg border transition"
                style={{
                    borderColor: 'rgba(104,23,147,0.35)',
                    background: 'rgba(104,23,147,0.08)',
                    color: 'var(--reseau-violet, #681793)',
                }}
            >
                {premium?.active ? <Sparkles className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                Suggérer avec l'IA
                {!premium?.active && <span className="ml-1 opacity-70">· Premium</span>}
            </button>

            {upsell && (
                <div
                    data-testid="bio-ai-upsell"
                    className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    role="dialog"
                    onClick={() => setUpsell(false)}
                >
                    <div onClick={(e) => e.stopPropagation()}
                         className="w-full max-w-md rounded-2xl p-6 shadow-2xl"
                         style={{ background: 'var(--reseau-surface, #fff)', color: 'var(--reseau-ink)' }}>
                        <div className="flex items-start gap-3 mb-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                                 style={{ background: 'rgba(104,23,147,0.1)', color: 'var(--reseau-violet)' }}>
                                <Lock className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-lg font-bold">Fonction réservée Premium</h3>
                                <p className="text-sm mt-1" style={{ color: 'var(--reseau-ink-muted)' }}>
                                    L'assistant IA de bio est inclus avec Homly Réseau Premium (19 € HT/mois).
                                    Écrivez une bio percutante en 30 secondes, gardez la main sur le résultat.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-4">
                            <button type="button" onClick={() => setUpsell(false)}
                                    data-testid="bio-ai-upsell-close"
                                    className="text-sm px-3 py-2 rounded-lg"
                                    style={{ color: 'var(--reseau-ink-muted)' }}>
                                Plus tard
                            </button>
                            <button type="button"
                                    onClick={() => { setUpsell(false); navigate('/reseau/parametres'); }}
                                    data-testid="bio-ai-upsell-cta"
                                    className="reseau-btn reseau-btn-primary px-4 py-2 rounded-lg text-sm font-semibold"
                                    style={{ background: 'var(--reseau-violet)', color: '#fff' }}>
                                Découvrir Premium
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {open && (
                <div
                    data-testid="bio-ai-dialog"
                    className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                    role="dialog"
                    onClick={() => setOpen(false)}
                >
                    <div onClick={(e) => e.stopPropagation()}
                         className="w-full max-w-xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
                         style={{ background: 'var(--reseau-surface, #fff)', color: 'var(--reseau-ink)' }}>
                        <div className="p-5 border-b flex items-start justify-between"
                             style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                            <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                                     style={{ background: 'rgba(104,23,147,0.1)', color: 'var(--reseau-violet)' }}>
                                    <Sparkles className="w-5 h-5" />
                                </div>
                                <div>
                                    <p className="text-[11px] font-semibold uppercase tracking-wider"
                                       style={{ color: 'var(--reseau-violet)' }}>Assistant IA · Premium</p>
                                    <h3 className="text-lg font-bold">Générer 3 propositions de bio</h3>
                                    <p className="text-xs mt-0.5" style={{ color: 'var(--reseau-ink-muted)' }}>
                                        Vous restez seul maître du texte final.
                                    </p>
                                </div>
                            </div>
                            <button type="button" onClick={() => setOpen(false)}
                                    data-testid="bio-ai-dialog-close"
                                    aria-label="Fermer"
                                    className="p-1.5 rounded-lg hover:bg-black/5">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="p-5 space-y-4">
                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider mb-2 block"
                                       style={{ color: 'var(--reseau-ink-muted)' }}>
                                    Ton
                                </label>
                                <div className="flex flex-wrap gap-1.5" data-testid="bio-ai-tone-selector">
                                    {TONES.map((t) => (
                                        <button key={t.value} type="button"
                                                onClick={() => setTone(t.value)}
                                                data-testid={`bio-ai-tone-${t.value}`}
                                                className="px-3 py-1.5 rounded-full text-xs font-medium border transition"
                                                style={tone === t.value ? {
                                                    background: 'var(--reseau-violet)', color: '#fff',
                                                    borderColor: 'var(--reseau-violet)',
                                                } : {
                                                    background: 'transparent',
                                                    color: 'var(--reseau-ink)',
                                                    borderColor: 'rgba(0,0,0,0.15)',
                                                }}>
                                            {t.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="text-xs font-semibold uppercase tracking-wider mb-2 block"
                                       style={{ color: 'var(--reseau-ink-muted)' }}
                                       htmlFor="bio-ai-hint">
                                    Ce que vous voulez mettre en avant (facultatif)
                                </label>
                                <textarea id="bio-ai-hint"
                                          value={hint} onChange={(e) => setHint(e.target.value)}
                                          maxLength={400} rows={3}
                                          data-testid="bio-ai-hint"
                                          placeholder="Ex : accompagnement des primo-accédants, 12 ans sur Lyon 3, spécialiste des biens de charme…"
                                          className="w-full px-3 py-2 rounded-lg border text-sm outline-none"
                                          style={{ borderColor: 'rgba(0,0,0,0.15)', background: '#fff' }} />
                            </div>

                            <button type="button"
                                    onClick={generate} disabled={loading}
                                    data-testid="bio-ai-generate-btn"
                                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold"
                                    style={{ background: 'var(--reseau-violet)', color: '#fff', opacity: loading ? 0.7 : 1 }}>
                                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                {suggestions.length ? 'Regénérer' : 'Générer 3 propositions'}
                            </button>

                            {suggestions.length > 0 && (
                                <div className="space-y-2" data-testid="bio-ai-suggestions">
                                    {suggestions.map((s, i) => (
                                        <div key={i}
                                             data-testid={`bio-ai-suggestion-${i}`}
                                             className="p-4 rounded-xl border"
                                             style={{ borderColor: 'rgba(0,0,0,0.1)', background: 'rgba(104,23,147,0.03)' }}>
                                            <p className="text-sm leading-relaxed"
                                               style={{ color: 'var(--reseau-ink)' }}>
                                                {s}
                                            </p>
                                            <div className="flex items-center justify-between mt-3">
                                                <span className="text-[11px]"
                                                      style={{ color: 'var(--reseau-ink-muted)' }}>
                                                    {s.length} caractères
                                                </span>
                                                <button type="button" onClick={() => applyOne(s)}
                                                        data-testid={`bio-ai-apply-${i}`}
                                                        className="text-xs font-semibold px-3 py-1.5 rounded-lg"
                                                        style={{ background: 'var(--reseau-violet)', color: '#fff' }}>
                                                    Utiliser cette bio
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                    <button type="button" onClick={generate} disabled={loading}
                                            className="inline-flex items-center gap-1.5 text-xs mt-1"
                                            style={{ color: 'var(--reseau-ink-muted)' }}>
                                        <RefreshCw className="w-3 h-3" /> Regénérer 3 nouvelles variantes
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
